import { Controller } from "@hotwired/stimulus"

// Test: log immediately when file is loaded
console.log("[RegisterDayValidation] FILE LOADED - controller class defined")

// Connects to data-controller="register-day-validation"
export default class extends Controller {
  static targets = ["form", "dateField", "submitButton"]
  static values = {
    debounceMs: { type: Number, default: 500 }
  }

  connect() {
    // ALERT TEST - if you see this, the controller is connecting
    if (typeof window !== 'undefined' && window.alert) {
      console.log("RegisterDayValidation controller is connecting...")
    }
    // Force console log - this should ALWAYS appear if controller connects
    console.log("========================================")
    console.log("[RegisterDayValidation] CONTROLLER CONNECTED!")
    console.log("[RegisterDayValidation] Element:", this.element)
    console.log("[RegisterDayValidation] Element ID:", this.element?.id)
    console.log("[RegisterDayValidation] Element classes:", this.element?.className)
    console.log("[RegisterDayValidation] Form target:", this.hasFormTarget ? "found" : "NOT FOUND")
    console.log("[RegisterDayValidation] DateField target:", this.hasDateFieldTarget ? "found" : "NOT FOUND")
    console.log("[RegisterDayValidation] SubmitButton target:", this.hasSubmitButtonTarget ? "found" : "NOT FOUND")
    console.log("========================================")
    
    // Test if window.console exists
    if (!window.console) {
      alert("Console not available!")
      return
    }
    
    // Test if we can find the form
    const formById = document.getElementById("register-day-form")
    console.log("[RegisterDayValidation] Form by ID:", formById ? "FOUND" : "NOT FOUND")
    
    const formByController = document.querySelector('[data-controller*="register-day-validation"]')
    console.log("[RegisterDayValidation] Form by controller attribute:", formByController ? "FOUND" : "NOT FOUND")
    
    // Use event delegation to catch all clicks on "Verifica" buttons
    // This works even for dynamically added rows
    const formElement = this.hasFormTarget ? this.formTarget : this.element
    console.log("[RegisterDayValidation] Setting up event delegation on:", formElement)
    console.log("[RegisterDayValidation] Form element ID:", formElement.id)
    console.log("[RegisterDayValidation] Form element classes:", formElement.className)
    
    // Use capture phase to catch events early
    // Use event delegation - listen to ALL clicks in the form
    const clickHandler = (e) => {
      if (e.target.closest("[data-register-day-movement-info-open]")) {
        return
      }

      console.log("[RegisterDayValidation] ===== CLICK EVENT DETECTED =====")
      console.log("[RegisterDayValidation] Target:", e.target)
      console.log("[RegisterDayValidation] Target tagName:", e.target.tagName)
      console.log("[RegisterDayValidation] Target textContent:", e.target.textContent?.trim())
      
      // Check if clicked element or its parent is a "Verifica" button
      let button = e.target
      
      // If target is not a button, find the closest button
      if (button.tagName !== 'BUTTON') {
        button = e.target.closest('button')
      }
      
      console.log("[RegisterDayValidation] Button found:", button)
      
      if (button) {
        const actionAttr = button.getAttribute("data-action") || ""
        // Non intercettare il pulsante info modale (stesso form, altra azione Stimulus)
        if (actionAttr.includes("register-day-movement-info")) {
          return
        }

        const buttonText = button.textContent.trim()
        console.log("[RegisterDayValidation] Button text:", buttonText)
        console.log("[RegisterDayValidation] Button data-action:", button.getAttribute('data-action'))
        
        // Check if it's a Verifica button
        if (buttonText.includes("Verifica") || button.getAttribute('data-action')?.includes('validateRow')) {
          console.log("[RegisterDayValidation] ===== VERIFICA BUTTON CLICKED! =====")
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          
          // Call validateRow directly
          setTimeout(() => {
            this.validateRowFromButton(button)
          }, 0)
        } else {
          console.log("[RegisterDayValidation] Not a Verifica button, ignoring")
        }
      } else {
        console.log("[RegisterDayValidation] No button found")
      }
    }
    
    formElement.addEventListener("click", clickHandler, true) // Use capture phase
    console.log("[RegisterDayValidation] Event listener added with capture phase")
    
    // Also try without capture phase as fallback
    formElement.addEventListener("click", clickHandler, false)
    console.log("[RegisterDayValidation] Event listener added without capture phase")
    
    // Test: try to find all Verifica buttons and log them
    setTimeout(() => {
      const allButtons = formElement.querySelectorAll('button')
      console.log("[RegisterDayValidation] Found", allButtons.length, "buttons in form")
      allButtons.forEach((btn, i) => {
        console.log(`[RegisterDayValidation] Button ${i}:`, btn.textContent.trim(), "data-action:", btn.getAttribute('data-action'))
      })
      
      const verificaButtons = Array.from(allButtons).filter(btn => btn.textContent.trim().includes("Verifica"))
      console.log("[RegisterDayValidation] Found", verificaButtons.length, "Verifica buttons")
      
      // Test: add direct click handler to first Verifica button as test
      if (verificaButtons.length > 0) {
        const testButton = verificaButtons[0]
        console.log("[RegisterDayValidation] Adding direct click handler to test button:", testButton)
        testButton.addEventListener("click", (e) => {
          console.log("[RegisterDayValidation] ===== DIRECT CLICK HANDLER FIRED =====")
          e.preventDefault()
          e.stopPropagation()
          this.validateRowFromButton(testButton)
        })
      }
    }, 1000)
  }
  
  // Alternative method called directly from event delegation
  validateRowFromButton(button) {
    console.log("[RegisterDayValidation] ===== validateRowFromButton CALLED =====")
    console.log("[RegisterDayValidation] Button:", button)
    console.log("[RegisterDayValidation] Button disabled?", button.disabled)
    console.log("[RegisterDayValidation] Button text:", button.textContent)
    
    // Prevent if already processing
    if (button.disabled) {
      console.log("[RegisterDayValidation] Button already disabled, ignoring")
      return
    }
    
    try {
      // Create a synthetic event object
      const syntheticEvent = {
        currentTarget: button,
        target: button,
        preventDefault: () => {},
        stopPropagation: () => {}
      }
      
      console.log("[RegisterDayValidation] Calling validateRow with synthetic event")
      this.validateRow(syntheticEvent)
    } catch (error) {
      console.error("[RegisterDayValidation] ERROR in validateRowFromButton:", error)
      console.error("[RegisterDayValidation] Error stack:", error.stack)
    }
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  debouncedValidate() {
    console.log("[RegisterDayValidation] debouncedValidate called")
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    
    this.debounceTimer = setTimeout(() => {
      console.log("[RegisterDayValidation] Debounce timeout, calling validate()")
      this.validate()
    }, this.debounceMsValue)
  }

  validate() {
    console.log("[RegisterDayValidation] Starting validation...")
    
    // Use element directly if form target not found (fallback)
    const formElement = this.hasFormTarget ? this.formTarget : this.element
    
    // Get form data
    const formData = new FormData(formElement)
    
    // Get date
    const date = formData.get("date") || this.dateFieldTarget?.value
    
    if (!date) {
      console.log("[RegisterDayValidation] Skipping validation - no date")
      this.hideAllFeedback()
      return
    }

    // Extract all movements from form
    const movements = this.extractMovements(formData)
    
    if (movements.length === 0) {
      console.log("[RegisterDayValidation] Skipping validation - no movements")
      this.hideAllFeedback()
      return
    }

    console.log("[RegisterDayValidation] Validating", movements.length, "movements")

    // Show loading state
    this.showLoading()

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    
    // Validate via AJAX
    fetch("/movements/validate_day", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken || ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ 
        date: date,
        movements: movements 
      })
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`HTTP error! status: ${response.status}, body: ${text}`)
          })
        }
        return response.json()
      })
      .then(data => {
        console.log("[RegisterDayValidation] Validation response:", data)
        this.handleValidationResponse(data)
      })
      .catch(error => {
        console.error("[RegisterDayValidation] Error validating movements:", error)
        this.showError("Errore durante la validazione. Riprova.")
      })
  }

  extractMovements(formData) {
    const movements = []
    const movementsMap = new Map()
    
    // Rails form format: movements[INDEX][field] or movements[INDEX][movement_destinations_attributes][0][field]
    // Iterate through all form entries
    for (const [key, value] of formData.entries()) {
      // Match pattern: movements[INDEX][FIELD] or movements[INDEX][movement_destinations_attributes][0][FIELD]
      const match = key.match(/^movements\[(\d+)\]\[(.+)\]$/)
      if (match) {
        const index = match[1]
        const fieldPath = match[2]
        
        if (!movementsMap.has(index)) {
          movementsMap.set(index, {})
        }
        
        const movement = movementsMap.get(index)
        
        // Handle nested fields like movement_destinations_attributes[0][destination_zone_id]
        const nestedMatch = fieldPath.match(/^movement_destinations_attributes\[0\]\[(\w+)\]$/)
        if (nestedMatch) {
          const destField = nestedMatch[1]
          if (!movement.movement_destinations_attributes) {
            movement.movement_destinations_attributes = [{}]
          }
          if (!movement.movement_destinations_attributes[0]) {
            movement.movement_destinations_attributes[0] = {}
          }
          movement.movement_destinations_attributes[0][destField] = value
        } else {
          // Regular field (origin_zone_id, notes, time, etc.)
          movement[fieldPath] = value
        }
      }
    }
    
    // Convert map to array, filter out empty movements
    for (const [index, movement] of movementsMap.entries()) {
      // If this movement is tied to a mandatory AvailableMovement, propagate
      // the current \"not needed\" checkbox state into the payload so that
      // the server-side validator can skip it.
      const availableMovementId = movement.available_movement_id
      if (availableMovementId) {
        const excludedValue = formData.get(`exclusions[${availableMovementId}][excluded]`)
        if (excludedValue === "1") {
          movement.excluded = "1"
        }
      }
      // Skip if no destination_zone_id
      const dest = movement.movement_destinations_attributes?.[0]
      if (dest && dest.destination_zone_id && dest.destination_zone_id.trim() !== "") {
        // Ensure input_type has a default
        if (!dest.input_type) {
          dest.input_type = "tons"
        }
        movements.push(movement)
      }
    }
    
    console.log("[RegisterDayValidation] Extracted movements:", movements)
    
    return movements
  }

  handleValidationResponse(data) {
    // Hide loading
    this.hideLoading()

    // Process each movement result
    if (data.movements && Array.isArray(data.movements)) {
      data.movements.forEach(result => {
        this.updateMovementRow(result.index, result)
      })
    }

    // Update overall status
    if (data.valid) {
      this.showValidStatus()
      this.enableSubmit()
    } else {
      this.showInvalidStatus(data.total_errors)
      this.disableSubmit()
    }
  }

  updateMovementRow(index, result) {
    // Find the movement row by index
    const row = this.findMovementRow(index)
    if (!row) return

    // Remove existing feedback
    this.removeRowFeedback(row)

    // Show errors if any
    if (result.errors && result.errors.length > 0) {
      this.showRowErrors(row, result.errors)
    }

    // Show warnings if any
    if (result.warnings && result.warnings.length > 0) {
      this.showRowWarnings(row, result.warnings)
    }

    // Show preview if available
    if (result.preview && result.preview.destinations && result.preview.destinations.length > 0) {
      this.showRowPreview(row, result.preview)
    }

    // Update row border color
    if (result.valid) {
      row.classList.remove("border-red-300")
      row.classList.add("border-green-300")
    } else {
      row.classList.remove("border-green-300")
      row.classList.add("border-red-300")
    }
  }

  findMovementRow(index) {
    // Find row by looking for input with name="movements[INDEX][...]"
    const formElement = this.hasFormTarget ? this.formTarget : this.element
    const input = formElement.querySelector(`input[name="movements[${index}][origin_zone_id]"], select[name="movements[${index}][origin_zone_id]"]`)
    if (!input) return null
    
    // Find the parent movement-row
    return input.closest('.movement-row')
  }

  removeRowFeedback(row) {
    // Remove ALL feedback elements (both client-side and server-side)
    // This prevents duplication when switching between global and local validation
    
    // Strategy: Remove all divs at the start of the row that look like feedback messages
    // Keep removing until we find the grid container (the actual form fields)
    let child = row.firstElementChild
    while (child) {
      const nextSibling = child.nextElementSibling
      
      // Check if this is a feedback div (error, warning, or preview)
      const isFeedbackDiv = (
        child.tagName === 'DIV' && (
          child.classList.contains('bg-red-50') || 
          child.classList.contains('bg-yellow-50') || 
          child.classList.contains('bg-blue-50') || 
          child.classList.contains('bg-green-50') ||
          child.hasAttribute('data-validation-feedback')
        )
      )
      
      // Also check if it's the grid container (the actual form)
      const isGridContainer = child.classList.contains('grid')
      
      if (isFeedbackDiv) {
        child.remove()
      } else if (isGridContainer) {
        // Stop when we reach the grid container (form fields)
        break
      }
      
      child = nextSibling
    }
  }

  showRowErrors(row, errors) {
    const errorsHtml = errors.map(error => `<li>${this.escapeHtml(error)}</li>`).join("")
    const errorsDiv = document.createElement('div')
    errorsDiv.setAttribute('data-validation-feedback', 'errors')
    errorsDiv.className = "mb-3 bg-red-50 border border-red-200 rounded-lg p-3"
    errorsDiv.innerHTML = `
      <ul class="list-disc list-inside text-sm text-red-700">
        ${errorsHtml}
      </ul>
    `
    row.insertBefore(errorsDiv, row.firstChild)
  }

  showRowWarnings(row, warnings) {
    const warningsHtml = warnings.map(warning => `<li>${this.escapeHtml(warning)}</li>`).join("")
    const warningsDiv = document.createElement('div')
    warningsDiv.setAttribute('data-validation-feedback', 'warnings')
    warningsDiv.className = "mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3"
    warningsDiv.innerHTML = `
      <ul class="list-disc list-inside text-sm text-yellow-700">
        ${warningsHtml}
      </ul>
    `
    row.insertBefore(warningsDiv, row.firstChild)
  }

  showRowPreview(row, preview) {
    const destinationsHtml = preview.destinations.map(dest => {
      let icon = "✓"
      let colorClass = "text-green-600"
      let bgClass = "bg-green-50 border-green-200"
      
      if (dest.action === "error") {
        icon = "✗"
        colorClass = "text-red-600"
        bgClass = "bg-red-50 border-red-200"
      } else if (dest.action === "create_process") {
        icon = "➕"
        colorClass = "text-blue-600"
        bgClass = "bg-blue-50 border-blue-200"
      } else if (dest.action === "add_to_process") {
        icon = "↗"
        colorClass = "text-blue-600"
        bgClass = "bg-blue-50 border-blue-200"
      }
      
      return `
        <div class="${bgClass} border rounded-lg p-2 text-xs">
          <span class="${colorClass} font-semibold">${icon}</span>
          <span class="text-gray-700">${this.escapeHtml(dest.zone_name)}: ${this.escapeHtml(dest.message)}</span>
        </div>
      `
    }).join("")
    
    const previewDiv = document.createElement('div')
    previewDiv.setAttribute('data-validation-feedback', 'preview')
    previewDiv.className = "mb-3 space-y-1"
    previewDiv.innerHTML = destinationsHtml
    row.insertBefore(previewDiv, row.firstChild)
  }


  disableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  enableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  showError(message) {
    // Could show a global error message
    console.error("[RegisterDayValidation]", message)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // Validate a single row - ONLY the row that was clicked
  validateRow(event) {
    // Get button from event (Stimulus provides currentTarget)
    const button = event.currentTarget || event.target
    
    if (!button) {
      console.error("[RegisterDayValidation] Button not found", event)
      return
    }
    
    console.log("[RegisterDayValidation] Button clicked:", button)
    console.log("[RegisterDayValidation] Button text:", button.textContent)
    
    // Prevent double-click
    if (button.disabled) {
      console.log("[RegisterDayValidation] Button already processing, ignoring click")
      return
    }
    
    // Find the row - button should be inside a movement-row
    let row = button.closest('.movement-row')
    
    // If closest doesn't work, try traversing up
    if (!row) {
      let current = button.parentElement
      let attempts = 0
      while (current && attempts < 15) {
        if (current.classList && current.classList.contains('movement-row')) {
          row = current
          console.log("[RegisterDayValidation] Found row by traversing up, depth:", attempts)
          break
        }
        current = current.parentElement
        attempts++
      }
    }
    
    if (!row) {
      console.error("[RegisterDayValidation] Row not found for button, trying alternative approach")
      console.error("[RegisterDayValidation] Button:", button)
      console.error("[RegisterDayValidation] Button parent:", button.parentElement)
      
      // Alternative: find row by looking for the closest container with movement data
      // The button is inside a flex container, which is inside the grid, which is inside the row
      let current = button
      for (let i = 0; i < 10; i++) {
        current = current.parentElement
        if (!current) break
        
        // Check if this element or any ancestor has movement-row class
        if (current.classList && current.classList.contains('movement-row')) {
          row = current
          console.log("[RegisterDayValidation] Found row by alternative method at depth", i)
          break
        }
        
        // Also check if it contains inputs with movement data
        const hasMovementInputs = current.querySelector && current.querySelector('input[name*="movements["], select[name*="movements["]')
        if (hasMovementInputs) {
          // This might be the row container, try to find the actual row
          const possibleRow = current.closest('.movement-row') || current.querySelector('.movement-row')
          if (possibleRow) {
            row = possibleRow
            console.log("[RegisterDayValidation] Found row by movement inputs at depth", i)
            break
          }
        }
      }
    }
    
    if (!row) {
      console.error("[RegisterDayValidation] Still could not find row")
      // Last resort: try to extract index from button and find row by index
      const buttonIndex = button.getAttribute('data-movement-index')
      if (buttonIndex) {
        const formElement = this.hasFormTarget ? this.formTarget : this.element
        const inputWithIndex = formElement.querySelector(`input[name="movements[${buttonIndex}][origin_zone_id]"], select[name="movements[${buttonIndex}][origin_zone_id]"]`)
        if (inputWithIndex) {
          row = inputWithIndex.closest('.movement-row')
          if (row) {
            console.log("[RegisterDayValidation] Found row by index lookup:", buttonIndex)
          }
        }
      }
    }
    
    if (!row) {
      console.error("[RegisterDayValidation] Could not find row after all attempts")
      return
    }
    
    console.log("[RegisterDayValidation] Row found:", row)
    
    // Get the movement index - try multiple sources
    let movementIndex = row.getAttribute('data-movement-index') || button.getAttribute('data-movement-index')
    
    console.log("[RegisterDayValidation] Initial movementIndex from attributes:", movementIndex)
    
    if (!movementIndex || movementIndex === 'TEMPLATE_INDEX') {
      // Try to extract from the first input name in this row
      const firstInput = row.querySelector('input[name*="[origin_zone_id]"], select[name*="[origin_zone_id]"]')
      console.log("[RegisterDayValidation] First input found:", firstInput)
      if (firstInput && firstInput.name) {
        console.log("[RegisterDayValidation] First input name:", firstInput.name)
        const match = firstInput.name.match(/movements\[(\d+)\]/)
        if (match) {
          movementIndex = match[1]
          console.log("[RegisterDayValidation] Extracted index from input name:", movementIndex)
          // Update row and button data attributes for future use
          row.setAttribute('data-movement-index', movementIndex)
          button.setAttribute('data-movement-index', movementIndex)
        } else {
          console.error("[RegisterDayValidation] Could not extract movement index from input name:", firstInput.name)
          // Try alternative: look for any input/select in the row
          const anyInput = row.querySelector('input[name*="movements["], select[name*="movements["]')
          if (anyInput && anyInput.name) {
            console.log("[RegisterDayValidation] Trying alternative input:", anyInput.name)
            const altMatch = anyInput.name.match(/movements\[(\d+)\]/)
            if (altMatch) {
              movementIndex = altMatch[1]
              console.log("[RegisterDayValidation] Extracted index from alternative input:", movementIndex)
              row.setAttribute('data-movement-index', movementIndex)
              button.setAttribute('data-movement-index', movementIndex)
            } else {
              console.error("[RegisterDayValidation] Could not extract index from alternative input either")
            this.showRowStatus(row, false, ["Impossibile determinare l'indice della riga"])
            button.disabled = false
            if (button.dataset.originalClasses) {
              button.classList.remove('opacity-50')
              button.className = button.dataset.originalClasses
              delete button.dataset.originalClasses
            } else if (button.dataset.originalText) {
              button.textContent = button.dataset.originalText
              delete button.dataset.originalText
            }
            return
            }
          } else {
            console.error("[RegisterDayValidation] No alternative input found")
            this.showRowStatus(row, false, ["Impossibile trovare i campi della riga"])
            button.disabled = false
            if (button.dataset.originalClasses) {
              button.classList.remove('opacity-50')
              button.className = button.dataset.originalClasses
              delete button.dataset.originalClasses
            } else if (button.dataset.originalText) {
              button.textContent = button.dataset.originalText
              delete button.dataset.originalText
            }
            return
          }
        }
      } else {
        console.error("[RegisterDayValidation] Could not find input to extract movement index")
        this.showRowStatus(row, false, ["Impossibile trovare i campi della riga"])
        button.disabled = false
        if (button.dataset.originalClasses) {
          button.classList.remove('opacity-50')
          button.className = button.dataset.originalClasses
          delete button.dataset.originalClasses
        } else if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText
          delete button.dataset.originalText
        }
        return
      }
    }
    
    console.log("[RegisterDayValidation] Validating ONLY row with index:", movementIndex)
    
    // Update button data attribute for future use
    button.setAttribute('data-movement-index', movementIndex)

    // Disable button and show loading state
    // Check if button contains an icon (SVG) instead of text
    const hasIcon = button.querySelector('svg') !== null
    button.disabled = true
    
    if (hasIcon) {
      // Store original classes BEFORE adding loading state
      button.dataset.originalClasses = button.className
      // For icon buttons, add loading class instead of changing text
      button.classList.add('opacity-50')
    } else {
      // For text buttons, change text as before
      const originalText = button.textContent
      button.textContent = "Verifica..."
      button.dataset.originalText = originalText
    }
    
    // Get form data
    const formElement = this.hasFormTarget ? this.formTarget : this.element
    const formData = new FormData(formElement)
    
    // Get date
    const date = formData.get("date") || (this.hasDateFieldTarget ? this.dateFieldTarget.value : null)
    
    // Extract ONLY this row's movement data
    const movement = this.extractSingleMovement(formData, movementIndex)
    
    console.log("[RegisterDayValidation] Extracted movement data for row", movementIndex, ":", movement)
    
    console.log("[RegisterDayValidation] Proceeding with AJAX call...")
    console.log("[RegisterDayValidation] Movement data:", movement)

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    console.log("[RegisterDayValidation] CSRF token:", csrfToken ? "found" : "NOT FOUND")
    
    // Convert movement_destinations_attributes from object to array for Rails compatibility
    const movementForServer = { ...movement }
    if (movementForServer.movement_destinations_attributes && typeof movementForServer.movement_destinations_attributes === 'object' && !Array.isArray(movementForServer.movement_destinations_attributes)) {
      // Convert object with numeric keys to array
      movementForServer.movement_destinations_attributes = Object.keys(movementForServer.movement_destinations_attributes)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(key => movementForServer.movement_destinations_attributes[key])
    }
    
    console.log("[RegisterDayValidation] ===== SENDING AJAX REQUEST =====")
    console.log("[RegisterDayValidation] URL: /movements/validate_single")
    console.log("[RegisterDayValidation] Date:", date)
    console.log("[RegisterDayValidation] Movement data:", JSON.stringify(movementForServer, null, 2))
    
    // Validate via AJAX
    fetch("/movements/validate_single", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken || ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ 
        date: date,
        movement: movementForServer
      })
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`HTTP error! status: ${response.status}, body: ${text}`)
          })
        }
        return response.json()
      })
      .then(data => {
        console.log("[RegisterDayValidation] Validation response for row", movementIndex, ":", data)
        // Show status ONLY for this specific row
        this.showRowStatus(row, data.valid, data.errors || [], data.warnings || [], data.preview)
      })
      .catch(error => {
        console.error("[RegisterDayValidation] ===== AJAX ERROR =====")
        console.error("[RegisterDayValidation] Error type:", error.constructor.name)
        console.error("[RegisterDayValidation] Error message:", error.message)
        console.error("[RegisterDayValidation] Error stack:", error.stack)
        this.showRowStatus(row, false, ["Errore durante la validazione. Riprova."])
      })
      .finally(() => {
        // Always re-enable button, even if there was an error
        button.disabled = false
        // Restore button state based on whether it's an icon or text button
        if (button.dataset.originalClasses) {
          // Remove loading class first
          button.classList.remove('opacity-50')
          // Restore original classes
          button.className = button.dataset.originalClasses
          delete button.dataset.originalClasses
        } else if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText
          delete button.dataset.originalText
        }
        console.log("[RegisterDayValidation] Button re-enabled for row", movementIndex)
      })
  }

  extractSingleMovement(formData, index) {
    const movement = {}
    const indexStr = String(index)
    
    console.log("[RegisterDayValidation] Extracting movement for index:", indexStr)
    
    // Extract ONLY fields for this specific movement index
    for (const [key, value] of formData.entries()) {
      // Only process movements fields
      if (!key.startsWith(`movements[${indexStr}]`)) continue
      
      console.log("[RegisterDayValidation] Processing field:", key, "=", value)
      
      // Match: movements[0][movement_destinations_attributes][0][destination_zone_id]
      // or: movements[0][origin_zone_id]
      // or: movements[0][notes]
      // or: movements[0][time]
      
      // Check if it's a nested destination field
      const destMatch = key.match(/^movements\[\d+\]\[movement_destinations_attributes\]\[(\d+)\]\[(\w+)\]$/)
      if (destMatch) {
        const destIndex = destMatch[1]
        const destField = destMatch[2]
        
        if (!movement.movement_destinations_attributes) {
          movement.movement_destinations_attributes = {}
        }
        if (!movement.movement_destinations_attributes[destIndex]) {
          movement.movement_destinations_attributes[destIndex] = {}
        }
        movement.movement_destinations_attributes[destIndex][destField] = value
        console.log("[RegisterDayValidation] Added destination field [", destIndex, "][", destField, "] =", value)
        continue
      }
      
      // Check if it's a regular field: movements[0][field_name]
      const regularMatch = key.match(/^movements\[\d+\]\[(\w+)\]$/)
      if (regularMatch) {
        const fieldName = regularMatch[1]
        movement[fieldName] = value
        console.log("[RegisterDayValidation] Added regular field:", fieldName, "=", value)
        continue
      }
      
      console.log("[RegisterDayValidation] Field not matched:", key)
    }
    
    // Ensure input_type has a default
    if (movement.movement_destinations_attributes) {
      Object.keys(movement.movement_destinations_attributes).forEach(destIndex => {
        if (!movement.movement_destinations_attributes[destIndex].input_type) {
          movement.movement_destinations_attributes[destIndex].input_type = "tons"
          console.log("[RegisterDayValidation] Set default input_type to 'tons' for destination", destIndex)
        }
      })
    }

    // If this movement is tied to a mandatory AvailableMovement, propagate
    // the current \"not needed\" checkbox state into the payload so that
    // the server-side validator can skip it.
    if (movement.available_movement_id) {
      const excludedValue = formData.get(`exclusions[${movement.available_movement_id}][excluded]`)
      if (excludedValue === "1") {
        movement.excluded = "1"
      }
    }
    
    console.log("[RegisterDayValidation] Final extracted movement for index", indexStr, ":", JSON.stringify(movement, null, 2))
    
    // Check if we have destination data
    if (movement.movement_destinations_attributes) {
      const firstDest = Object.values(movement.movement_destinations_attributes)[0]
      if (firstDest) {
        console.log("[RegisterDayValidation] Destination zone ID:", firstDest.destination_zone_id)
        console.log("[RegisterDayValidation] Input value:", firstDest.input_value)
      }
    }
    
    return movement
  }

  showRowStatus(row, valid, errors = [], warnings = [], preview = null) {
    // Remove ALL existing feedback (both client-side and server-side)
    // This ensures no duplication when switching between global and local validation
    this.removeRowFeedback(row)
    
    // Update row border
    row.classList.remove("border-red-300", "border-green-300", "border-yellow-300", "border-gray-300")

    if (!valid) {
      // Invalid - red border with errors
      row.classList.add("border-red-300")
      if (errors.length > 0) {
        this.showRowErrors(row, errors)
      }
    } else if (warnings && warnings.length > 0) {
      // Structurally valid but with warnings (e.g. partial quantity)
      // → use yellow border to distinguish from fully OK rows.
      row.classList.add("border-yellow-300")
      this.showRowWarnings(row, warnings)
    } else {
      // Fully valid, no warnings
      row.classList.add("border-green-300")
    }

    // Show preview if available
    if (preview && preview.destinations && preview.destinations.length > 0) {
      this.showRowPreview(row, preview)
    }
  }
}
